import isEqual from 'lodash/isEqual';
import { Howl } from 'howler';
import { AlertPriority } from '@ixicare/resources';
import { alertSounds } from '~config/soundNotification';

export const DEFAULT_MUTE_REQUEST_ID = '1234';

/** Reserved muteRequests id reflecting the user's manual presence state.
 *  Pushed by an external reaction (see Presence store wiring in app init)
 *  so SoundService stays free of store coupling. */
export const PRESENCE_MUTE_REQUEST_ID = 'presence';

/** Reserved muteRequests id reflecting passive (view-only) alert mode — an
 *  observer who hasn't joined an HCP group isn't alerted. Independent of the
 *  presence id so the two silence reasons stack without clobbering each other. */
export const PASSIVE_MUTE_REQUEST_ID = 'passive';

class SoundService {
  Types = AlertPriority;

  private activeSoundTypes: AlertPriority[] = [];

  private playingSoundType: AlertPriority | undefined;
  private playingSound: Howl | undefined;

  // The initial mute request is so that the user has to interact (browser restrictions)
  public muteRequests: string[] = [DEFAULT_MUTE_REQUEST_ID];

  private soundCache = new Map<AlertPriority, Howl>();

  private muteListeners = new Set<() => void>();

  /** Subscribe to mute-state changes (any cause: user click, presence,
   *  auto-play unlock, reset). Returns an unsubscribe fn. */
  public subscribe(listener: () => void): () => void {
    this.muteListeners.add(listener);
    return () => this.muteListeners.delete(listener);
  }

  private emitMuteChange() {
    this.muteListeners.forEach((cb) => cb());
  }

  canAutoPlay = (() => {
    if (typeof window === 'undefined') return Promise.resolve(false);
    return import('can-autoplay')
      .then((module) => module.default.audio())
      .then(({ result }) => {
        // Only lift the autoplay gate: presence/passive requests pushed before
        // this resolved must survive, or a passive observer starts ringing.
        if (result && this.muteRequests.includes(DEFAULT_MUTE_REQUEST_ID)) {
          this.muteRequests = this.muteRequests.filter((mr) => mr !== DEFAULT_MUTE_REQUEST_ID);
          this.emitMuteChange();
          this.start();
        }
        return result;
      });
  })();

  updateActiveSoundTypes(updatedSoundTypes: AlertPriority[]) {
    console.info('SoundService', 'update', updatedSoundTypes.join(','));
    if (isEqual(this.activeSoundTypes, updatedSoundTypes)) return;
    this.activeSoundTypes = updatedSoundTypes;

    this.start();
  }

  public start = () => {
    console.info('SoundService', 'start', !this.muteRequests.length ? 'allowed' : 'declined');
    if (this.muteRequests.length) return;

    const soundTypeToPlay = this.activeSoundTypes.find((a) => a);

    // If were still playing the same sound, check if it hasn't stagnated
    if (this.playingSound && !this.playingSound.playing() && this.playingSoundType === soundTypeToPlay) {
      this.playingSound.play();
    }

    if (soundTypeToPlay !== this.playingSoundType) {
      this.playingSound?.stop();
      this.playingSound = undefined;
      this.playingSoundType = soundTypeToPlay;

      if (soundTypeToPlay) {
        if (this.soundCache.has(soundTypeToPlay)) {
          this.playingSound = this.soundCache.get(soundTypeToPlay);
          this.playingSound?.play();
        } else {
          this.playingSound = new Howl({
            src: `/assets/sounds/${alertSounds[soundTypeToPlay]}`,
            autoplay: true,
            loop: true,
          });
          this.playingSound.on('load', this.handleSoundLoaded);
          this.soundCache.set(soundTypeToPlay, this.playingSound);
        }
      }
    }
  };

  private handleSoundLoaded = (error: any) => {
    if (error) {
      console.error('SoundService failed to load sound', error);
      this.playingSound?.unload();
      if (this.playingSoundType) {
        this.soundCache.delete(this.playingSoundType);
      }
      this.playingSound = undefined;
      this.playingSoundType = undefined;
      return;
    }
    if (!this.playingSound) return;
    console.info('SoundService', 'load', this.playingSoundType);
  };

  public stop() {
    console.info('SoundService', 'stop');
    if (this.playingSound) {
      this.playingSound.stop();
    }
    this.playingSoundType = undefined;
    this.playingSound = undefined;
  }

  public reset() {
    console.info('SoundService', 'reset');
    this.stop();
    this.activeSoundTypes = [];
    this.muteRequests = [];
    this.emitMuteChange();
  }

  public mute() {
    this.stop();
    const id = Math.round(Math.random() * 1e6).toString();
    console.info('SoundService', 'mute', id);
    this.muteRequests.push(id);
    this.emitMuteChange();
    return id;
  }

  public unmute(id: string) {
    if (this.muteRequests.includes(id)) {
      console.info('SoundService', 'unmute', id);
      this.muteRequests = this.muteRequests.filter((mr) => mr !== id);
      this.emitMuteChange();
      this.start();
    }
  }

  /** Tie sound output to the user's presence silence state. Called by a
   *  mobx reaction in the dashboard's app init; idempotent. */
  public setPresenceSilenced(silenced: boolean) {
    this.setReservedSilenced(PRESENCE_MUTE_REQUEST_ID, silenced);
  }

  /** Tie sound output to passive (view-only) alert mode. Same stacking contract
   *  as presence silencing; called from an effect on the observer mode. */
  public setPassiveSilenced(silenced: boolean) {
    this.setReservedSilenced(PASSIVE_MUTE_REQUEST_ID, silenced);
  }

  private setReservedSilenced(id: string, silenced: boolean) {
    const has = this.muteRequests.includes(id);
    if (silenced && !has) {
      this.muteRequests.push(id);
      this.emitMuteChange();
      this.stop();
    } else if (!silenced && has) {
      this.muteRequests = this.muteRequests.filter((mr) => mr !== id);
      this.emitMuteChange();
      this.start();
    }
  }
}

export default new SoundService();
